import { useEffect, useRef } from 'react'
import type { Post } from '../types'
import { CLOUD_API } from '../config'

declare global {
  interface Window {
    CloudChat?: {
      init: (config: Record<string, unknown>) => void
      destroy?: () => void
      setContext?: (ctx: Record<string, unknown>) => void
      toggle?: () => void
      open?: () => void
      close?: () => void
    }
    _cloudChatLoaded?: boolean
  }
}

interface AgentPanelProps {
  post?: Post
}

const THEME = {
  '--cloudchat-bg': '#FFFFFF',
  '--cloudchat-bg-secondary': '#F8F8F8',
  '--cloudchat-bg-header': '#FFFFFF',
  '--cloudchat-text': '#333333',
  '--cloudchat-text-muted': '#999999',
  '--cloudchat-accent': '#C41E3A',
  '--cloudchat-user-bg': '#C41E3A',
  '--cloudchat-user-text': '#FFFFFF',
  '--cloudchat-border': '#E5E5E5',
  '--cloudchat-success': '#16A34A',
  '--cloudchat-radius': '0px',
  '--cloudchat-font': 'Roboto, sans-serif',
}

export function AgentPanel({ post }: AgentPanelProps) {
  const postRef = useRef(post)
  postRef.current = post

  // Load script + init ONCE (global guard prevents StrictMode double-init)
  useEffect(() => {
    // Tenant-safe: the agent widget lives on the instance's cloud-api.
    // Without VITE_CLOUD_API there is no agent backend → panel stays inert
    // instead of loading the operator's widget.
    if (!CLOUD_API) return
    if (window._cloudChatLoaded) return
    window._cloudChatLoaded = true

    const script = document.createElement('script')
    script.src = `${CLOUD_API}/widget/cloud-chat.js`
    script.async = true
    script.onload = () => {
      window.CloudChat?.init({
        session: 'ContentWorker',
        mode: 'queue',
        title: 'Content Assistent',
        display: 'bubble',
        server: CLOUD_API,
        theme: THEME,
      })
      // Set context if post already loaded
      const p = postRef.current
      if (p) {
        setTimeout(() => window.CloudChat?.setContext?.({ post_id: p.id }), 1000)
      }
    }
    document.body.appendChild(script)
  }, [])

  // Set context when post changes
  useEffect(() => {
    if (!post || !window.CloudChat?.setContext) return
    window.CloudChat.setContext({ post_id: post.id })
  }, [post?.id])

  return null
}
